// M69e — taxonomy override store + resolver.
//
// Admin-editable label/color overrides layered over the code-defined tag maps.
// The code stays the source of truth for STRUCTURE (item types drive TS-typed
// routing/NFA — not editable); this only overrides PRESENTATION.
//
//   DB `taxonomy_overrides`  ──load once at boot──▶  _store (module map)
//                                                       │
//   itemTypeLabel / itemTypeColor / itemTypeChipSx ─────┤  every accessor
//   chipMaps label/color fns · venueTypeLabel ──────────┘  consults _store,
//                                                          falling back to the
//                                                          code value.
//
// The map is populated once by <TaxonomyProvider> at app boot (and refetched on
// window focus). Accessors read it synchronously — NO per-render DB reads.
// A code-default flash before the first load is accepted; a failed load is
// non-blocking (falls back to the code maps).

import type { SxProps, Theme } from '@mui/material/styles';
import type { ChipColor } from './chipMaps';

export interface TaxonomyOverrideRow {
  family: string;
  value: string;
  label: string | null;
  color_light: string | null;
  color_dark: string | null;
  /** M91 (1A) — retired from the pickers and the parsers, but STILL RENDERED
   *  wherever it is already stored: hiding a value must never blank an item
   *  that has it. Consulted for EXTENSIBLE families only — a hidden row on
   *  `item_type` is inert, because losing Firearm from the type picker would
   *  break routing that is compile-time anyway. */
  hidden: boolean;
  /** `'code'` = an override layered on a value this app defines; `'admin'` = a
   *  value that exists ONLY because an admin added it. The difference decides
   *  what un-hiding means (a DELETE vs an UPDATE) and who may be removed. */
  source: 'code' | 'admin';
}

const PALETTE_TOKENS = ['primary', 'secondary', 'info', 'success', 'warning', 'error', 'default'] as const;
const isHex = (c: string | null | undefined): c is string => !!c && /^#[0-9A-Fa-f]{6}$/.test(c);
const isToken = (c: string | null | undefined): c is ChipColor =>
  !!c && (PALETTE_TOKENS as readonly string[]).includes(c);
/** A color the resolver can safely render (guards OV#6: an unparseable stored value must not break rendering). */
const isValidColor = (c: string | null | undefined): boolean => isHex(c) || isToken(c);

const keyOf = (family: string, value: string) => `${family}:${value}`;

const _store = new Map<string, TaxonomyOverrideRow>();

/** What the LOADER may hand in. `hidden`/`source` are optional here and
 *  required on the stored row: the boundary tolerates a payload without them
 *  (a client cached from before M91, a select that forgot a column), while
 *  every reader inside gets a definite answer. */
export type TaxonomyOverrideInput =
  Omit<TaxonomyOverrideRow, 'hidden' | 'source'>
  & Partial<Pick<TaxonomyOverrideRow, 'hidden' | 'source'>>;

/** Replace the whole override store. Called by <TaxonomyProvider> when the DB rows load / change. */
export function setTaxonomyOverrides(rows: readonly TaxonomyOverrideInput[]): void {
  _store.clear();
  for (const r of rows) {
    if (!r.family || !r.value) continue;
    // Defensive: drop colors the resolver can't render (belt-and-suspenders with the DB CHECK).
    _store.set(keyOf(r.family, r.value), {
      family: r.family,
      value: r.value,
      label: r.label ?? null,
      color_light: isValidColor(r.color_light) ? r.color_light : null,
      color_dark: isValidColor(r.color_dark) ? r.color_dark : null,
      hidden: r.hidden === true,
      source: r.source === 'admin' ? 'admin' : 'code',
    });
  }
}

/** Test seam. */
export function _clearTaxonomyOverrides(): void {
  _store.clear();
}

export function getTaxonomyOverride(family: string, value: string): TaxonomyOverrideRow | undefined {
  return _store.get(keyOf(family, value));
}

/** True when this tag has any override — drives the "overridden" indicator + Reset affordance. */
export function hasTaxonomyOverride(family: string, value: string): boolean {
  return _store.has(keyOf(family, value));
}

/** Override label wins; else the code label. */
export function resolveLabel(family: string, value: string, codeLabel: string): string {
  return _store.get(keyOf(family, value))?.label ?? codeLabel;
}

/** If the override sets a palette TOKEN color, use it; else the code color. (Hex overrides go through resolveChipSx.) */
export function resolveColor(family: string, value: string, codeColor: ChipColor): ChipColor {
  const o = _store.get(keyOf(family, value));
  if (o && isToken(o.color_light)) return o.color_light;
  return codeColor;
}

/**
 * If the override sets HEX colors (the color picker's output), build a theme-aware
 * sx matching itemTypeChipSx's shape (light hex on light mode, dark hex on dark);
 * else the code sx. Unparseable values already dropped in setTaxonomyOverrides.
 */
export function resolveChipSx(
  family: string,
  value: string,
  codeSx: SxProps<Theme> | undefined,
): SxProps<Theme> | undefined {
  const o = _store.get(keyOf(family, value));
  if (o && isHex(o.color_light) && isHex(o.color_dark)) {
    const light = o.color_light;
    const dark = o.color_dark;
    const pick = (t: Theme) => (t.palette.mode === 'dark' ? dark : light);
    return { color: pick, borderColor: pick, '& .MuiChip-label': { color: pick } };
  }
  return codeSx;
}

// ─── M91 (1A/16A) — the extension layer ──────────────────────────────────────
//
// Two families (`color`, `optic_kind`) are EXTENSIBLE: an admin can add values
// and retire code ones from /admin/tags. Everything else is structure the code
// owns, and asks none of these questions.
//
//     offered = (code values − hidden) + (admin extras − hidden)
//
// deduped case-insensitively, because "Sniper Grey" and "sniper grey" are one
// color spelled twice and offering both is the drift this epic exists to end.

/** Is this value retired? Meaningful for extensible families; the callers are
 *  the ones that know, and every other family simply never asks. */
export function isTaxonomyValueHidden(family: string, value: string): boolean {
  return _store.get(keyOf(family, value))?.hidden === true;
}

/** The values an admin ADDED to this family — never the code ones. Hidden
 *  extras are dropped here rather than by every caller. */
export function addedTaxonomyValues(
  family: string,
  /** The ADMIN PAGE passes true: a hidden value still has to be listed there,
   *  or there is no way to bring it back. Pickers pass nothing. */
  includeHidden = false,
): TaxonomyOverrideRow[] {
  const out: TaxonomyOverrideRow[] = [];
  for (const r of _store.values()) {
    if (r.family === family && r.source === 'admin' && (includeHidden || !r.hidden)) out.push(r);
  }
  // Stable, alphabetical: the picker shows them in one group at the bottom, and
  // Map iteration order is insertion order, i.e. whatever the fetch returned.
  return out.sort((a, b) => (a.label ?? a.value).localeCompare(b.label ?? b.value));
}

/**
 * The offered vocabulary for an extensible family: the code list minus the
 * retired values, then the admin extras that are not already a code twin.
 *
 * `toEntry` builds the caller's own option shape from an admin row, so this
 * knows nothing about swatches or magnification ladders.
 */
export function vocabularyFor<T extends { value: string }>(
  family: string,
  codeValues: readonly T[],
  toEntry: (row: TaxonomyOverrideRow) => T,
): T[] {
  const kept = codeValues.filter((v) => !isTaxonomyValueHidden(family, v.value));
  const seen = new Set(kept.map((v) => v.value.toLowerCase()));
  const extras: T[] = [];
  for (const r of addedTaxonomyValues(family)) {
    const key = r.value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    extras.push(toEntry(r));
  }
  return [...kept, ...extras];
}
